import { PlatformRequirementDocumentVerdict } from '@client/graphql/__generated__/types';
import { match, P } from 'ts-pattern';
import {
  ChecklistContractDocumentView,
  ChecklistEvaluationDocumentView,
  ChecklistGroupedEvaluationDocumentView,
  ChecklistIssueView,
} from './checklistIssue.types';

/**
 * How checklist issues collapse into the rows a surface actually draws.
 *
 * Pure logic in a leaf module so a caller can count rows without importing the
 * component that renders them — a heading that disagrees with the list beneath
 * it is the count drift the compliance surfaces are meant to avoid.
 */

/** One rendered checklist row: a requirement, as seen by a single evaluation run. */
export interface ChecklistGroup {
  groupKey: string;
  renderGroupKey: string;
  problemType: ChecklistIssueView['problemType'];
  evaluatedAt: unknown;
  issues: ChecklistIssueView[];
  checklistItemName: string;
  checklistSectionName?: string | null;
  allowedDocumentCategories: string[];
  allowedForms: string[];
  evaluationDocuments: ChecklistGroupedEvaluationDocumentView[];
}

/**
 * The identity that decides which row an issue lands in: its checklist
 * requirement AND the evaluation run that produced it. Rows from different runs
 * must never merge even when the checklist activity id is unchanged.
 *
 * Shared by the grouper and the counter so a heading can never disagree with
 * the list beneath it.
 */
type ChecklistGroupIdentity = Pick<
  ChecklistIssueView,
  'groupKey' | 'evaluationRunId'
>;

const checklistGroupRenderKey = (issue: ChecklistGroupIdentity): string =>
  JSON.stringify([issue.groupKey, issue.evaluationRunId]);

const documentKey = (
  document: ChecklistEvaluationDocumentView,
  fallbackKey: string
): string => {
  if (
    document.ingestionDocumentId != null ||
    document.platformDocumentId != null
  ) {
    return `${document.ingestionDocumentId ?? ''}|${
      document.platformDocumentId ?? ''
    }|${document.documentName}`;
  }

  return fallbackKey;
};

const contractDocumentKey = (
  contractDocument: ChecklistContractDocumentView
): string =>
  JSON.stringify([contractDocument.form, contractDocument.documentCategory]);

const mergeContractDocuments = (
  target: ChecklistContractDocumentView[],
  source: ChecklistContractDocumentView[]
): void => {
  const seen = new Set(target.map(contractDocumentKey));
  for (const contractDocument of source) {
    const key = contractDocumentKey(contractDocument);
    if (!seen.has(key)) {
      target.push({ ...contractDocument });
      seen.add(key);
    }
  }
};

/**
 * Groups issues by their external checklist requirement AND evaluation run.
 * Rows from different runs must never be merged even when the checklist activity
 * id is unchanged, so the render key combines `groupKey` with `evaluationRunId`.
 * Within a group, evaluation documents are unioned/de-duplicated (OR-ing mismatch)
 * and the requirement's allowed categories/forms are de-duplicated. Matches the
 * agent surface's semantics so ignore/resubmit stay aligned at the
 * checklist-activity boundary.
 */
export const groupChecklistIssues = (
  issues: ChecklistIssueView[]
): ChecklistGroup[] => {
  const byKey = new Map<string, ChecklistGroup>();

  for (const issue of issues) {
    const renderGroupKey = checklistGroupRenderKey(issue);
    const group: ChecklistGroup = byKey.get(renderGroupKey) ?? {
      groupKey: issue.groupKey,
      renderGroupKey,
      // Every issue in a group shares one checklist requirement and therefore
      // one kind of problem; the first is representative.
      problemType: issue.problemType,
      evaluatedAt: issue.evaluatedAt,
      issues: [],
      checklistItemName: issue.checklistItemName,
      checklistSectionName: issue.checklistSectionName,
      allowedDocumentCategories: [],
      allowedForms: [],
      evaluationDocuments: [],
    };

    group.issues.push(issue);
    group.allowedDocumentCategories = [
      ...new Set([
        ...group.allowedDocumentCategories,
        ...issue.allowedDocumentCategories,
      ]),
    ];
    group.allowedForms = [
      ...new Set([...group.allowedForms, ...issue.allowedForms]),
    ];

    for (const [
      documentIndex,
      document,
    ] of issue.evaluationDocuments.entries()) {
      const key = documentKey(
        document,
        `${issue.publicIssueId}:${documentIndex}:${document.documentName}`
      );
      const existingIndex = group.evaluationDocuments.findIndex(
        (candidate) => candidate.renderKey === key
      );
      if (existingIndex === -1) {
        group.evaluationDocuments.push({
          ...document,
          contractDocuments: [...document.contractDocuments],
          renderKey: key,
        });
      } else {
        const existing = group.evaluationDocuments[existingIndex];
        existing.isProblemDocument =
          existing.isProblemDocument || document.isProblemDocument;
        existing.verdict = match({
          existingVerdict: existing.verdict,
          incomingVerdict: document.verdict,
        })
          .with(
            {
              existingVerdict: PlatformRequirementDocumentVerdict.MISMATCHED,
            },
            () => PlatformRequirementDocumentVerdict.MISMATCHED
          )
          .with(
            {
              incomingVerdict: PlatformRequirementDocumentVerdict.MISMATCHED,
            },
            () => PlatformRequirementDocumentVerdict.MISMATCHED
          )
          .with(
            { existingVerdict: P.nullish },
            ({ incomingVerdict }) => incomingVerdict ?? null
          )
          .otherwise(({ existingVerdict }) => existingVerdict ?? null);
        mergeContractDocuments(
          existing.contractDocuments,
          document.contractDocuments
        );
      }
    }

    byKey.set(renderGroupKey, group);
  }

  return [...byKey.values()];
};

/**
 * How many rows a checklist list will draw for these issues. Use this for any
 * heading or badge counting checklist issues: several issues on one checklist
 * requirement collapse into a single row, so the raw issue count overstates it.
 *
 * Counts identities directly rather than building the groups — a caller that
 * only needs the number should not pay for the document merge, and should not
 * need evaluation evidence it may not have loaded.
 */
export const countChecklistIssueGroups = (
  issues: ChecklistGroupIdentity[]
): number => new Set(issues.map(checklistGroupRenderKey)).size;
